/** Same shape as iOS `StreetViewMetadata`. */
export type StreetViewMetadata = {
  panoId: string
  panoLat: number
  panoLng: number
}

export class StreetViewMetadataError extends Error {
  constructor(
    message: string,
    readonly code: 'no_street_view' | 'invalid_response',
  ) {
    super(message)
    this.name = 'StreetViewMetadataError'
  }
}

/**
 * Nearest Street View panorama via Maps JS `StreetViewService`.
 *
 * Prefer this over the Street View Static metadata REST endpoint: browser keys
 * with HTTP referrer restrictions cannot call that REST API (REQUEST_DENIED).
 * Heading toward the property is computed the same way as iOS.
 */
export async function fetchStreetViewNearProperty(
  streetViewService: google.maps.StreetViewService,
  property: { lat: number; lng: number },
): Promise<StreetViewMetadata> {
  let data: google.maps.StreetViewPanoramaData
  try {
    const response = await streetViewService.getPanorama({
      location: property,
      radius: 100,
      preference: 'nearest',
    })
    data = response.data
  } catch {
    throw new StreetViewMetadataError(
      'No Street View available near this property',
      'no_street_view',
    )
  }

  const panoId = data.location?.pano
  const panoLatLng = data.location?.latLng
  if (!panoId || !panoLatLng) {
    throw new StreetViewMetadataError(
      'Invalid Street View panorama response',
      'invalid_response',
    )
  }

  return {
    panoId,
    panoLat: panoLatLng.lat(),
    panoLng: panoLatLng.lng(),
  }
}

/**
 * Bearing in degrees from `from` → `to` (0–360).
 * Same formula as iOS `calculateBearing`.
 */
export function calculateBearing(
  from: { lat: number; lng: number },
  to: { lat: number; lng: number },
): number {
  const lat1 = (from.lat * Math.PI) / 180
  const lat2 = (to.lat * Math.PI) / 180
  const deltaLng = ((to.lng - from.lng) * Math.PI) / 180

  const y = Math.sin(deltaLng) * Math.cos(lat2)
  const x =
    Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(deltaLng)

  let bearing = (Math.atan2(y, x) * 180) / Math.PI
  bearing = (bearing + 360) % 360
  return bearing
}
